import React from 'react'

import { cn } from './cn'

// DOM mirrors Semantic UI React's Label (elements/Label/Label.js) for the props the app uses.
// The class words come first, in SUIR's order (ui, color, pointing, size, basic, prompt,
// label), as marker classes, joined outside cn() as Button does. The styles are copied from
// Semantic UI 2.4.2 label.css for these variants. Pointing arrows and the Form-scoped prompt
// rule are not here: the arrows are `ui-label-pointing-above` and `ui-label-pointing-left` in
// globals.css, and `.ui.form .field .prompt.label` belongs to Form.

export type LabelColor = 'teal' | 'green'
export type LabelSize = 'small' | 'tiny'
export type LabelPointing = 'above' | 'left'

export type LabelProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'color' | 'content'
> & {
  basic?: boolean
  children?: React.ReactNode
  color?: LabelColor
  content?: React.ReactNode
  pointing?: LabelPointing
  prompt?: boolean
  size?: LabelSize
}

// `.ui.label`. Font size comes before line height, as in Semantic.
const LABEL_BASE = [
  '[&.ui]:inline-block [&.ui]:[font-size:0.85714286rem] [&.ui]:leading-[1]',
  '[&.ui]:align-baseline [&.ui]:m-[0em_0.14285714em] [&.ui]:py-[0.5833em] [&.ui]:px-[0.833em]',
  '[&.ui]:bg-[#e8e8e8] [&.ui]:[background-image:none] [&.ui]:text-[rgba(0,0,0,0.6)]',
  '[&.ui]:[text-transform:none] [&.ui]:font-bold [&.ui]:border-0 [&.ui]:border-solid',
  '[&.ui]:border-transparent [&.ui]:rounded-[0.28571429rem]',
  '[&.ui]:[transition:background_0.1s_ease]',
  '[&.ui:first-child]:ml-0 [&.ui:last-child]:mr-0',
].join(' ')

const BASIC = [
  '[&.ui.basic]:[background:none_#ffffff] [&.ui.basic]:border-[1px] [&.ui.basic]:border-solid',
  '[&.ui.basic]:border-[rgba(34,36,38,0.15)] [&.ui.basic]:text-[rgba(0,0,0,0.87)]',
  '[&.ui.basic]:[box-shadow:none]',
].join(' ')

// `.ui.teal.label` and `.ui.green.label` are !important, and the basic colour variants beat
// them by specificity, as in Semantic.
const COLOR: Record<LabelColor, string> = {
  teal: [
    '[&.ui.teal]:!bg-[#00b5ad] [&.ui.teal]:!border-[#00b5ad] [&.ui.teal]:!text-[#ffffff]',
    '[&.ui.basic.teal]:![background:none_#ffffff] [&.ui.basic.teal]:!border-[#00b5ad] [&.ui.basic.teal]:!text-[#00b5ad]',
  ].join(' '),
  green: [
    '[&.ui.green]:!bg-[#21ba45] [&.ui.green]:!border-[#21ba45] [&.ui.green]:!text-[#ffffff]',
    '[&.ui.basic.green]:![background:none_#ffffff] [&.ui.basic.green]:!border-[#21ba45] [&.ui.basic.green]:!text-[#21ba45]',
  ].join(' '),
}

const SIZE: Record<LabelSize, string> = {
  small: '[&.ui.small]:[font-size:0.78571429rem]',
  tiny: '[&.ui.tiny]:[font-size:0.71428571rem]',
}

// `.ui.pointing.label` (position and the 1em top margin of `pointing above`), and
// `.ui[class*="left pointing"].label` (no top margin, room for the arrow on the left). The
// arrows are in globals.css (`ui-label-pointing-above`, `ui-label-pointing-left`).
const POINTING: Record<LabelPointing, string> = {
  above: '[&.ui.pointing]:relative [&.ui.pointing]:mt-[1em]',
  left: '[&.ui.left.pointing]:relative [&.ui.left.pointing]:mt-0 [&.ui.left.pointing]:ml-[0.6666em]',
}

// SUIR's class words for each `pointing` value.
const POINTING_WORDS: Record<LabelPointing, string> = {
  above: 'pointing above',
  left: 'left pointing',
}

// `.ui.label.prompt` has no rule of its own in Semantic (the Form-scoped rule is on Form).
export function Label({
  basic,
  children,
  className,
  color,
  content,
  pointing,
  prompt,
  size,
  ...rest
}: LabelProps) {
  const markers = [
    'ui',
    color,
    pointing && POINTING_WORDS[pointing],
    size,
    basic && 'basic',
    prompt && 'prompt',
    'label',
    pointing && `ui-label-pointing-${pointing}`,
  ]
    .filter(Boolean)
    .join(' ')

  // SUIR renders `children` when they are set, and `content` otherwise.
  const body = children === undefined || children === null ? content : children

  return (
    <div
      {...rest}
      className={`${markers} ${cn(
        LABEL_BASE,
        color && COLOR[color],
        size && SIZE[size],
        basic && BASIC,
        pointing && POINTING[pointing],
        className,
      )}`}
    >
      {body}
    </div>
  )
}
